// DEV-only hook-order guard.
//
// Drop `useHookCountGuard("Component")` immediately above an early-return
// in any component and it will warn whenever the number of hooks called
// above it differs from the previous render — i.e. exactly the
// "Rendered fewer/more hooks than during the previous render" footgun
// that React itself only catches once it has already crashed.
//
// How: at module load (DEV only) we wrap React's internal dispatcher
// with a Proxy that bumps a per-render counter every time a `useXxx`
// hook is invoked. React swaps `ReactCurrentDispatcher.current` at the
// start of every component render, so the setter resets the counter
// for each new render frame. The guard hook then simply samples that
// counter and compares it to its own previous-render sample.
//
// In production this file is a no-op (`useHookCountGuard` does nothing).
import * as React from "react";
import { useRef } from "react";

let hookCount = 0;

if (import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SECRET = (React as any)
    .__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
  const dispatcherRef = SECRET?.ReactCurrentDispatcher;
  if (dispatcherRef && !dispatcherRef.__hookCountPatched) {
    dispatcherRef.__hookCountPatched = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const proxyCache = new WeakMap<object, any>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrap = (target: any) =>
      new Proxy(target, {
        get(t, prop, recv) {
          const value = Reflect.get(t, prop, recv);
          if (
            typeof value === "function" &&
            typeof prop === "string" &&
            prop.startsWith("use")
          ) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (...args: any[]) => {
              hookCount += 1;
              return value.apply(t, args);
            };
          }
          return value;
        },
      });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let raw: any = dispatcherRef.current;
    Object.defineProperty(dispatcherRef, "current", {
      configurable: true,
      get() {
        return raw;
      },
      set(next) {
        // React swaps the dispatcher at every component-render boundary,
        // so this is the cleanest place to reset the per-render counter.
        hookCount = 0;
        if (!next) {
          raw = next;
          return;
        }
        let proxied = proxyCache.get(next);
        if (!proxied) {
          proxied = wrap(next);
          proxyCache.set(next, proxied);
        }
        raw = proxied;
      },
    });
  }
}

/**
 * Place this as the LAST hook in a component, right before any early
 * return that depends on props/state. It records how many hooks ran
 * above it and warns (once per change) if that number ever shifts
 * between renders — the leading indicator of a hook-order crash.
 */
export function useHookCountGuard(name: string): void {
  const prevRef = useRef<number | null>(null);
  if (!import.meta.env.DEV) return;
  // hookCount has already been bumped by `useRef` above — that's fine,
  // we only care that the number stays the same across renders.
  const current = hookCount;
  const prev = prevRef.current;
  prevRef.current = current;
  if (prev !== null && prev !== current) {
    // eslint-disable-next-line no-console
    console.warn(
      `[useHookCountGuard:${name}] hook count changed ${prev} → ${current}. ` +
        `A hook above the early return is being added/removed conditionally — ` +
        `React will throw "Rendered fewer/more hooks than during the previous render" next.`,
    );
  }
}